import type { ApiError } from '../types/api'

const BASE_URL = '/api/tecdoc'

function toCamelCase(obj: unknown): unknown {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(toCamelCase)
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const camelKey = key.charAt(0).toLowerCase() + key.slice(1)
    result[camelKey] = toCamelCase(value)
  }
  return result
}
const MAX_RETRIES = 2
const RETRY_DELAY_MS = 1000

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${BASE_URL}${path}`
  let lastError: ApiError | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS * attempt))
    }

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        ...options,
      })

      if (!response.ok) {
        const errorBody = await response.text()
        let message = `HTTP ${response.status}`
        try {
          const parsed = JSON.parse(errorBody)
          message = parsed.error || parsed.message || message
        } catch {
          // errorBody wasn't JSON
        }

        lastError = {
          status: response.status,
          message,
          retryable: response.status >= 500,
        }

        if (!lastError.retryable) break
        continue
      }

      const raw = await response.json()
      // Handle both PascalCase (.NET default) and camelCase
      const success = raw.success ?? raw.Success
      const data = raw.data ?? raw.Data
      const error = raw.error ?? raw.Error

      if (!success) {
        throw {
          status: 422,
          message: error ?? 'Unknown error',
          retryable: false,
        } satisfies ApiError
      }

      return toCamelCase(data) as T
    } catch (err) {
      if ((err as ApiError).retryable !== undefined) {
        lastError = err as ApiError
        if (!lastError.retryable) break
        continue
      }
      // Network error — retryable
      lastError = {
        status: 0,
        message: 'Network error — check your connection',
        retryable: true,
      }
    }
  }

  throw lastError!
}

export function get<T>(path: string): Promise<T> {
  return request<T>(path)
}

export function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' })
}
